/**
 * Frame side of the widget <-> overlay postMessage bridge.
 *
 * Every inbound message is checked against the `parent_origin` the widget put
 * in the iframe URL; framing itself is restricted separately by the
 * `frame-ancestors` allowlist in nginx, so an attacker cannot get here by
 * claiming an arbitrary parent origin.
 */

import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  PROTOCOL_VERSION,
  isHostToFrameMessage,
  type FrameToHostMessage,
  type ThemeMode,
} from '../embedProtocol';

export interface EmbedSettings {
  query: string;
  filters: Record<string, string[]>;
  tokens: Record<string, string>;
  theme: ThemeMode;
  uiLanguage?: string;
  fontCssUrl?: string;
  overlayTitle?: string;
  overlayLogoUrl?: string;
  history: string[];
}

export interface EmbedBridge {
  /** True once the host has sent its first `es:init`. */
  ready: boolean;
  /** True when this document is actually running inside the widget overlay. */
  embedded: boolean;
  settings: EmbedSettings | null;
  /** Query pushed by the host after init (host bar submitted again). */
  pushedQuery: { value: string; nonce: number } | null;
  close(): void;
  navigate(url: string, newTab?: boolean): void;
  addHistory(query: string): void;
}

function readParentOrigin(): string | null {
  const raw = new URLSearchParams(window.location.search).get('parent_origin');
  if (!raw) return null;
  try {
    return new URL(raw).origin;
  } catch {
    return null;
  }
}

export function useEmbedBridge(): EmbedBridge {
  const parentOrigin = useMemo(readParentOrigin, []);
  const embedded = parentOrigin !== null && window.parent !== window;

  const [settings, setSettings] = useState<EmbedSettings | null>(null);
  const [pushedQuery, setPushedQuery] = useState<{ value: string; nonce: number } | null>(
    null
  );
  const nonceRef = useRef(0);

  const post = useCallback(
    (message: FrameToHostMessage) => {
      if (!embedded || !parentOrigin) return;
      window.parent.postMessage(message, parentOrigin);
    },
    [embedded, parentOrigin]
  );

  useEffect(() => {
    if (!embedded) return;

    function handleMessage(event: MessageEvent) {
      if (event.origin !== parentOrigin) return;
      if (event.source !== window.parent) return;
      if (!isHostToFrameMessage(event.data)) return;

      switch (event.data.type) {
        case 'es:init': {
          const message = event.data;
          setSettings({
            query: message.query,
            filters: message.filters ?? {},
            tokens: message.tokens ?? {},
            theme: message.theme,
            uiLanguage: message.uiLanguage,
            fontCssUrl: message.fontCssUrl,
            overlayTitle: message.overlayTitle,
            overlayLogoUrl: message.overlayLogoUrl,
            history: message.history ?? [],
          });
          break;
        }
        case 'es:query': {
          const message = event.data;
          nonceRef.current += 1;
          setPushedQuery({ value: message.query, nonce: nonceRef.current });
          setSettings((prev) =>
            prev
              ? { ...prev, query: message.query, filters: message.filters ?? prev.filters }
              : prev
          );
          break;
        }
        case 'es:theme': {
          const message = event.data;
          setSettings((prev) =>
            prev ? { ...prev, tokens: message.tokens, theme: message.theme } : prev
          );
          break;
        }
      }
    }

    window.addEventListener('message', handleMessage);
    post({ type: 'es:ready', version: PROTOCOL_VERSION });

    return () => window.removeEventListener('message', handleMessage);
  }, [embedded, parentOrigin, post]);

  const close = useCallback(() => {
    post({ type: 'es:close', version: PROTOCOL_VERSION });
  }, [post]);

  const navigate = useCallback(
    (url: string, newTab = false) => {
      post({ type: 'es:navigate', version: PROTOCOL_VERSION, url, newTab });
    },
    [post]
  );

  const addHistory = useCallback(
    (query: string) => {
      post({ type: 'es:history-add', version: PROTOCOL_VERSION, query });
    },
    [post]
  );

  return {
    ready: settings !== null,
    embedded,
    settings,
    pushedQuery,
    close,
    navigate,
    addHistory,
  };
}
